import { v4 as uuid } from 'uuid';
import Vue from 'vue';
import TYPES from '@/types';

// Application
import UpdateCustomerAddressCommand
  from '@/modules/user/customer-address/application/commands/update-customer-address-command';
import ChooseYourDocumentsService
  from '@/modules/user/customer-address/application/services/choose-your-documents-service';
import AddressService
  from '@/modules/user/customer-address/application/services/address-service';
import ProofOfAddressService
  from '@/modules/user/customer-address/application/services/proof-of-address-service';

// Domain
import { CustomerAddressDto }
  from '@/modules/user/customer-address/domain/dtos/customer-address-dto';
import Inject from '@/modules/shared/domain/di/inject';
import { Values } from '@/modules/shared/domain/i18n/types';
import Translator from '@/modules/shared/domain/i18n/translator';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';

export default class UpdateProofOfAddressModalViewModel {
  @Inject(TYPES.UPDATE_CUSTOMER_ADDRESS_COMMAND)
  private readonly update_customer_address_command!: UpdateCustomerAddressCommand;

  @Inject(TYPES.CHOOSE_YOUR_DOCUMENTS_SERVICE)
  private readonly choose_your_documents_service!: ChooseYourDocumentsService;

  @Inject(TYPES.ADDRESS_SERVICE)
  private readonly address_service!: AddressService;

  @Inject(TYPES.PROOF_OF_ADDRESS_SERVICE)
  private readonly proof_of_address_service!: ProofOfAddressService

  @Inject(TYPES.I18N)
  private readonly translator!: Translator;

  @Inject(TYPES.NOTIFIER)
  private readonly message_notifier!: MessageNotifier;

  readonly i18n_namespace = 'components.user-profile.user-profile-tabs.user-profile-my-documents-tab.update-proof-of-address.update-proof-of-address-modal';

  readonly STEPS = [
    'UpdateProofOfAddressChooseYourDocuments',
    'UpdateProofOfAddressData',
    'UpdateProofOfAddressAttachYourDocument',
  ];

  current_step = 0;

  final_step = this.STEPS.length - 1;

  current_component = this.STEPS[this.current_step];

  private readonly view!: Vue;

  public constructor(view: Vue) {
    this.view = view;
  }

  translate = (message: string, values?: Values) => this.translator.translate(`${this.i18n_namespace}.${message}`, values);

  nextStep = async () => {
    if (this.current_step < this.STEPS.length) {
      this.current_step += 1;
      this.current_component = this.STEPS[this.current_step];
    }
  }

  prevStep = () => {
    if (this.current_step > 0) {
      this.current_step -= 1;
      this.current_component = this.STEPS[this.current_step];
    }
  }

  getActiveClass = (index: number, is_title: boolean) => {
    const inactive_class = 'dark-grey';
    const active_class = 'secondary';
    if (is_title) {
      if (this.current_step >= index) {
        return active_class;
      }
      return inactive_class;
    }
    if (index < this.current_step) {
      return active_class;
    }
    return inactive_class;
  }

  updateProofOfAddress = async () => {
    try {
      const { selected_investment_product_ids, wealth_option_is_checked } = this
        .choose_your_documents_service.getChooseYourDocumentsData();
      const address_data = this.address_service.getAddressData();
      const proof_of_address = this.proof_of_address_service.getProofOfAddressData();
      const update_customer_address_payload: CustomerAddressDto = {
        request_id: uuid(),
        proof_of_address,
        investment_product_ids: selected_investment_product_ids,
        address: {
          street: address_data.street,
          external_number: address_data.external_number,
          interior_number: address_data.interior_number,
          zip_code: address_data.postal_code,
          municipality: address_data.municipality.descMunicipio,
          settlement: address_data.settlement.descLocalidad,
          city: address_data.municipality.descMunicipio,
          state: address_data.state.descestado,
          country: address_data.country.descpais,
        },
      };
      await this.update_customer_address_command.execute(update_customer_address_payload);
      this.clearCustomerAddressStates();
      this.view.$emit('openSuccessModal', wealth_option_is_checked);
    } catch {
      this.message_notifier.showErrorNotification('errors.update_customer_address_command');
    }
  }

  clearCustomerAddressStates = () => {
    this.choose_your_documents_service.setChooseYourDocumentsData({
      pocket_option_is_checked: false,
      wealth_option_is_checked: false,
      selected_investment_product_ids: [],
    });

    this.address_service.setAddressData({
      street: '',
      external_number: '',
      interior_number: '',
      postal_code: '',
      settlement: {
        codPais: '',
        descPais: '',
        codEstado: '',
        descEstado: '',
        codLocalidad: '',
        codMunicipio: '',
        codigoPostal: '',
        descLocalidad: '',
        descMunicipio: '',
      },
      municipality: {
        codPais: '',
        codEstado: '',
        codMunicipio: '',
        descMunicipio: '',
      },
      state: {
        codestado: '',
        descestado: '',
        codpais: '',
      },
      country: {
        descpais: 'MÉXICO',
        codpais: '412',
      },
    });

    this.proof_of_address_service.setProofOfAddressFileData(null);
    this.proof_of_address_service.setProofOfAddressData({
      name: '',
      mime_type: '',
      file_data: '',
    });
  }
}
